/**
 * Formatage français — déterministe, sans dépendance à la version d'ICU.
 *
 * Règles imposées (spécification § 5) :
 *   — arrondi `Math.round`, à la moitié supérieure ;
 *   — séparateur de milliers : espace fine insécable U+202F ;
 *   — séparateur décimal : virgule ;
 *   — signe négatif : moins typographique U+2212, jamais le trait d'union.
 *
 * L'implémentation n'utilise pas `toLocaleString` : le caractère produit comme
 * séparateur de milliers varie d'une version d'ICU à l'autre (U+00A0 sur
 * certaines, U+202F sur d'autres) et la spécification exige un rendu stable
 * « au caractère près ».
 *
 * Les caractères typographiques sont écrits en séquences d'échappement : un
 * caractère invisible dans le source se fait remplacer par une espace
 * ordinaire au premier copier-coller, et le bogue est alors indétectable à
 * la lecture.
 */
/** Espace fine insécable, séparateur de milliers (U+202F). */
export const SEP_MILLIERS = '\u202f';
/** Même espace fine insécable devant l'unité : « 205 852 € », « 51,3 % ». */
export const ESPACE_UNITE = '\u202f';
/** Signe moins typographique (U+2212). */
export const MOINS = '\u2212';
/** Fuseau de référence pour toutes les dates affichées. */
export const FUSEAU = 'Europe/Paris';
/**
 * Arrondi à la moitié supérieure, conforme à `Math.round`.
 * `Math.round(-0.5)` vaut `-0` : on normalise pour ne jamais afficher « −0 ».
 */
export function arrondi(n, decimales = 0) {
    const f = 10 ** decimales;
    const r = Math.round(n * f) / f;
    return Object.is(r, -0) ? 0 : r;
}
function groupe(entierNu) {
    let out = '';
    for (let i = 0; i < entierNu.length; i++) {
        if (i > 0 && (entierNu.length - i) % 3 === 0)
            out += SEP_MILLIERS;
        out += entierNu[i] ?? '';
    }
    return out;
}
/** Nombre au format français, sans unité. */
export function nombre(n, decimales = 0) {
    if (!Number.isFinite(n))
        throw new RangeError(`valeur non finie : ${String(n)}`);
    const r = arrondi(n, decimales);
    const negatif = r < 0;
    const abs = Math.abs(r).toFixed(decimales);
    const [ent = '0', dec] = abs.split('.');
    const corps = groupe(ent) + (dec ? `,${dec}` : '');
    return (negatif ? MOINS : '') + corps;
}
/** Montant en euros, arrondi à l'unité. */
export function euros(n) {
    return `${nombre(n, 0)}${ESPACE_UNITE}€`;
}
/** Montant en euros avec les centimes. */
export function eurosCentimes(n) {
    return `${nombre(n, 2)}${ESPACE_UNITE}€`;
}
/** Montant en milliers d'euros, une décimale. */
export function keuros(n) {
    return `${nombre(n / 1000, 1)}${ESPACE_UNITE}k€`;
}
/** Montant signé : « + » ou « − » explicite. */
export function eurosSigne(n) {
    const r = arrondi(n, 0);
    return (r >= 0 ? '+' : MOINS) + euros(Math.abs(r));
}
/** Pourcentage, une décimale par défaut. */
export function pourcent(ratio, decimales = 1) {
    return `${nombre(ratio * 100, decimales)}${ESPACE_UNITE}%`;
}
/**
 * Partie numérique d'un montant en milliers d'euros, sans son unité.
 *
 * Les gabarits qui affichent l'unité à part la découpaient de `keuros` par un
 * `split(' ')` — qui échoue, le séparateur d'unité étant une espace fine
 * insécable et non une espace ordinaire. Le résultat s'affichait « 63,3 k€ k€ ».
 */
export function keurosValeur(n) {
    return nombre(n / 1000, 1);
}
/** Entier simple (effectifs). */
export function entier(n) {
    return nombre(n, 0);
}
/** Date ISO `AAAA-MM-JJ` rendue en français. */
export function dateFr(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!m)
        throw new RangeError(`date ISO invalide : ${iso}`);
    const [, a, mo, j] = m;
    return `${j}/${mo}/${a}`;
}
/**
 * Noms des mois, écrits ici plutôt qu'obtenus d'`Intl` : la casse et les
 * abréviations d'ICU varient d'un environnement à l'autre, et un libellé
 * d'axe qui change entre le poste de travail et le serveur est un défaut.
 */
const MOIS = [
    'janvier',
    'février',
    'mars',
    'avril',
    'mai',
    'juin',
    'juillet',
    'août',
    'septembre',
    'octobre',
    'novembre',
    'décembre',
];
/** Mois ISO `AAAA-MM` rendu en français, par exemple « août 2026 ». */
export function moisFr(iso) {
    const m = /^(\d{4})-(\d{2})/.exec(iso);
    if (!m)
        throw new RangeError(`mois ISO invalide : ${iso}`);
    const [, annee, numero] = m;
    const nom = MOIS[Number(numero) - 1];
    if (nom === undefined)
        throw new RangeError(`mois ISO invalide : ${iso}`);
    return `${nom} ${annee}`;
}
/** Mois abrégé sur trois lettres, pour les axes serrés. */
export function moisCourt(iso) {
    const m = /^(\d{4})-(\d{2})/.exec(iso);
    if (!m)
        throw new RangeError(`mois ISO invalide : ${iso}`);
    const nom = MOIS[Number(m[2]) - 1];
    if (nom === undefined)
        throw new RangeError(`mois ISO invalide : ${iso}`);
    return nom.slice(0, 3);
}
/** Horodatage complet en français, fuseau Europe/Paris. */
export function horodatageFr(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        throw new RangeError(`horodatage invalide : ${iso}`);
    const parties = new Intl.DateTimeFormat('fr-FR', {
        timeZone: FUSEAU,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).formatToParts(d);
    const get = (t) => parties.find((p) => p.type === t)?.value ?? '';
    return `${get('day')}/${get('month')}/${get('year')} à ${get('hour')}h${get('minute')}`;
}
/**
 * Ancienneté d'un instantané, exprimée en clair.
 * `maintenantIso` est injecté pour garder la fonction déterministe.
 */
export function anciennete(isoInstantane, maintenantIso) {
    const a = new Date(isoInstantane).getTime();
    const b = new Date(maintenantIso).getTime();
    if (Number.isNaN(a) || Number.isNaN(b))
        throw new RangeError('horodatage invalide');
    const minutes = Math.max(0, Math.floor((b - a) / 60000));
    if (minutes < 60)
        return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    const heures = Math.floor(minutes / 60);
    if (heures < 24)
        return `${heures} heure${heures > 1 ? 's' : ''}`;
    const jours = Math.floor(heures / 24);
    return `${jours} jour${jours > 1 ? 's' : ''}`;
}
//# sourceMappingURL=format.js.map